/**
 * usePendingOps<Op> — the generic §3g Pattern A staging queue.
 *
 * Why this exists (2026-06-10, Session A4 / Fable-5 assessment pushback 1):
 * every detail page hand-rolled the same machinery per relationship type —
 * a useState<PendingXOp[]>, a failedKeys Set, a failedReasons Map, stage /
 * unstage callbacks, and clearing wired into three places (Cancel,
 * back-arrow, dirty-form-guard discard). Five-plus copies of that pattern
 * across AgreementDetailPage, AccountDetailPage and
 * AccountInsuranceDetailPage produced the C-109/C-110/C-111 class of
 * drift bugs. This hook makes the storage + failure bookkeeping a
 * one-liner so new affordances are Pattern A by construction.
 *
 * What deliberately STAYS in the page (not in this hook):
 *  - Save-handler ordering (field UPDATE → creates → detaches → lifecycle
 *    last) — business sequencing belongs to the page's transaction.
 *  - Domain staging rules (e.g. "staging a revoke on a pending assign just
 *    removes the assign", tier-flip toggling) — wrap `stage`/`unstage` in a
 *    page-level useCallback as before.
 *  - Maker-checker result handling + toast aggregation.
 *
 * Key semantics: `keyOf` must return a stable string per op. Staging an op
 * whose key already exists REPLACES it (gives singleton queues for free —
 * use a constant key for "at most one lifecycle op"). Unstage removes by
 * key and clears that key's failure marker.
 *
 * §3g cancel convention: call `clear()` from every discard path. The
 * page's dirty computation should include `!queue.isEmpty`.
 *
 * Charter: pure client-side state; no I/O, no storage, no telemetry.
 */
import { useCallback, useMemo, useState } from 'react';

export interface PendingOpsQueue<Op> {
  /** Staged ops, in staging order. */
  ops: readonly Op[];
  /** True when nothing is staged — include `!isEmpty` in the page's dirty check. */
  isEmpty: boolean;
  /** Stage an op; same-key ops are replaced in place (singleton-friendly). */
  stage: (op: Op) => void;
  /** Remove the op with this key; also clears its failure marker. */
  unstage: (key: string) => void;
  /** Drop everything — wire into Cancel / back-arrow / dirty-guard discard. */
  clear: () => void;
  /** Replace the queue wholesale (retry flows that keep only failed ops). */
  replace: (next: readonly Op[]) => void;
  /**
   * Escape hatch for domain staging rules that are more than append/remove
   * (e.g. "a revoke supersedes a pending tier-flip on the same row",
   * toggle-back semantics, in-place payload merging). Keeps the rule in the
   * page while the queue storage stays shared.
   */
  transform: (fn: (prev: readonly Op[]) => readonly Op[]) => void;
  /** Keys of ops that failed during the last Save batch. */
  failedKeys: ReadonlySet<string>;
  /** Failure reason per key (for inline row feedback). */
  failedReasons: ReadonlyMap<string, string>;
  /** Mark an op failed after a Save-batch error (keeps the op staged). */
  markFailed: (key: string, reason?: string) => void;
  /** Reset failure markers (start of a new Save attempt). */
  clearFailures: () => void;
  /**
   * Post-Save settlement in one call: drops succeeded ops from the queue,
   * keeps failed ops staged, and replaces the failure markers — the
   * "keep failed for retry" convention every detail page uses.
   */
  settle: (failedKeys: ReadonlySet<string>, reason?: string) => void;
  /** The page's keyOf, re-exposed so sections can compute row markers. */
  keyOf: (op: Op) => string;
}

export function usePendingOps<Op>(keyOf: (op: Op) => string): PendingOpsQueue<Op> {
  const [ops, setOps] = useState<readonly Op[]>([]);
  const [failures, setFailures] = useState<ReadonlyMap<string, string>>(new Map());

  const stage = useCallback((op: Op) => {
    setOps((prev) => {
      const k = keyOf(op);
      const idx = prev.findIndex((o) => keyOf(o) === k);
      if (idx >= 0) {
        const next = prev.slice();
        next[idx] = op;
        return next;
      }
      return [...prev, op];
    });
  }, [keyOf]);

  const unstage = useCallback((key: string) => {
    setOps((prev) => prev.filter((o) => keyOf(o) !== key));
    setFailures((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });
  }, [keyOf]);

  const clear = useCallback(() => {
    setOps([]);
    setFailures(new Map());
  }, []);

  const replace = useCallback((next: readonly Op[]) => {
    setOps(next);
  }, []);

  const transform = useCallback((fn: (prev: readonly Op[]) => readonly Op[]) => {
    setOps((prev) => fn(prev));
  }, []);

  const settle = useCallback((failedKeysIn: ReadonlySet<string>, reason?: string) => {
    setOps((prev) => prev.filter((o) => failedKeysIn.has(keyOf(o))));
    setFailures(() => {
      const next = new Map<string, string>();
      for (const k of failedKeysIn) {
        next.set(k, reason ?? 'Failed — see alert for details');
      }
      return next;
    });
  }, [keyOf]);

  const markFailed = useCallback((key: string, reason?: string) => {
    setFailures((prev) => {
      const next = new Map(prev);
      next.set(key, reason ?? 'Failed — see alert for details');
      return next;
    });
  }, []);

  const clearFailures = useCallback(() => {
    setFailures(new Map());
  }, []);

  const failedKeys = useMemo(() => new Set(failures.keys()), [failures]);

  return {
    ops,
    isEmpty: ops.length === 0,
    stage,
    unstage,
    clear,
    replace,
    transform,
    settle,
    failedKeys,
    failedReasons: failures,
    markFailed,
    clearFailures,
    keyOf,
  };
}
